import {
    GOOGLE_METADATA_BY_KEY,
    INSTAGRAM_METADATA_BY_KEY,
    NUMERICAL_NOTATION_TYPES,
    POPULARITY_METADATA_BY_KEY,
    TIKTOK_METADATA_BY_KEY,
} from './metrics';
import { formatUnixTimestampWeek, roundIfFinite } from './helpers';
import bugsnagClient from 'containers/BugsnagClient';
import convertToCsv from './exportCsv';
import { getStackedChartFns } from 'components/DataVis/utils';
import kebabCase from 'lodash/kebabCase';
import { tooltips } from '@/copy';

const defaultCSVColumns = [
    { key: 'name' },
    { key: 'volume' },
    { key: 'absoluteGrowth', label: 'increase' },
    { key: 'YoY' },
    { key: 'cluster' },
    { key: 'healthStats', label: 'health stats' },
];

// Category hierarchy columns for entity exports. cat2 is the broad taxonomy
// level and ships to every user; the finer cat3/cat4 levels stay internal-only.
// Only include these for category rows — hashtag and brand rows carry no
// `hierarchies` (the backend only populates it via get_categories_by_ids), so
// the columns would export empty.
export const getHierarchyCSVColumns = (isAdmin) => [
    { key: 'cat2' },
    ...(isAdmin ? [{ key: 'cat3' }, { key: 'cat4' }] : []),
];

const formatCell = (v) =>
    ['undefined', undefined, 'Insufficient Data', '-'].includes(v)
        ? null
        : typeof v === 'string'
          ? v.toLocaleLowerCase()
          : Array.isArray(v)
            ? v.toLocaleString().toLocaleLowerCase()
            : v;

export const formatItemsForCSV = (data, columns = defaultCSVColumns) => {
    let healthStatKeys;
    if (columns.some(({ key }) => key === 'healthStats')) {
        healthStatKeys = Array.from(
            data.reduce((acc, currentItem) => {
                const { healthStats } = currentItem;
                try {
                    Object.keys(healthStats).forEach(acc.add, acc);
                } catch (err) {
                    const { id, type, name } = currentItem || {};
                    bugsnagClient.notify(
                        new Error(
                            `Could not get health stats for "${name}" (${type}) with id: ${id}.`,
                        ),
                    );
                }
                return acc;
            }, new Set()),
        );
    }
    return data.reduce((acc, item, idx, ref) => {
        acc.push(
            columns
                .map(({ key, accessor }) => {
                    const value = accessor ? accessor(item) : item[key];
                    if (key === 'healthStats')
                        return healthStatKeys.map((metricLabel) =>
                            formatCell(
                                value && value[metricLabel]
                                    ? value[metricLabel].scoreLabel
                                    : null,
                            ),
                        );
                    if (key === 'flavors') {
                        const flavors =
                            item.flavors
                                ?.map(([_id, hierarchy]) => hierarchy?.cat4)
                                .join(', ') || '';
                        return formatCell(flavors);
                    }
                    if (typeof key === 'string' && key.indexOf('cat') > -1) {
                        const values =
                            item.hierarchies
                                ?.map(([_id, hierarchy]) => hierarchy[key])
                                .join(', ') || '';
                        return formatCell(values);
                    }
                    if (
                        [
                            NUMERICAL_NOTATION_TYPES.PERCENTAGE,
                            NUMERICAL_NOTATION_TYPES.PERCENTAGE_UNSIGNED,
                        ].some((notation) =>
                            [
                                GOOGLE_METADATA_BY_KEY[key]?.notation,
                                TIKTOK_METADATA_BY_KEY[key]?.notation,
                                POPULARITY_METADATA_BY_KEY[key]?.notation,
                                INSTAGRAM_METADATA_BY_KEY[key]?.notation,
                            ].includes(notation),
                        )
                    )
                        return formatCell(roundIfFinite(value, 3));
                    if (key === 'create_timestamp' && value)
                        return formatUnixTimestampWeek(value);
                    if (key === 'url') return value || '';
                    return formatCell(roundIfFinite(value));
                })
                .flat(),
        );

        if (idx === ref.length - 1) {
            acc.unshift(
                columns.reduce((acc, { key, label }) => {
                    if (key === 'healthStats') {
                        acc.push(
                            ...healthStatKeys.map((healthStatKey) =>
                                formatCell(
                                    tooltips[key][healthStatKey].subtitle,
                                ),
                            ),
                        );
                    } else {
                        acc.push(label || key);
                    }
                    return acc;
                }, []),
            );
        }

        return acc;
    }, []);
};

const defaultOptions = {
    columns: [{ key: 'name' }],
    tsColumns: [
        { key: 'date' },
        { key: 'real_volume', label: 'volume' },
        { key: 'prediction' },
        { key: 'backfill', label: 'estimated' },
    ],
};

export const formatItemTimeSeriesForCSV = (items, options) => {
    const { columns, tsColumns, skipPrediction } = {
        ...defaultOptions,
        ...options,
    };
    // Only surface the `estimated` (backfill) column when the export actually
    // contains backfilled points. Google search data is never backfilled, so it
    // would otherwise carry an always-empty column. Mirrors the per-source
    // behavior of formatItemPopularityTimeSeriesForCSV.
    const hasBackfill = items.some(({ timeSeries, time_series }) => {
        const ts = timeSeries || time_series;
        if (!(ts && ts.length)) return false;
        const backfillKey = ts[0]?.backfill;
        if (backfillKey == null) return false;
        return ts.slice(1).some((row) => row[backfillKey]);
    });
    // In markets that support the pop index we drop prediction entirely — both
    // the `prediction` flag column and the forecasted future rows — so the CSV
    // matches what the chart renders (see GoogleTimeSeries `skipPrediction`).
    const effectiveTsColumns = tsColumns.filter(({ key }) => {
        if (key === 'prediction' && skipPrediction) return false;
        if (key === 'backfill' && !hasBackfill) return false;
        return true;
    });
    const result = [];
    items.forEach(({ timeSeries, time_series, ...entity }) => {
        const ts = timeSeries || time_series;
        if (!(ts && ts.length)) return;

        if (result.length < 1)
            result.push(
                columns
                    .concat(effectiveTsColumns)
                    .map(({ key, label }) => label || key),
            );

        const header = ts.slice(0, 1).shift();
        const predictionKey = header?.prediction;
        ts.slice(1).forEach((row) => {
            if (skipPrediction && predictionKey != null && row[predictionKey])
                return;
            result.push(
                columns
                    .map(({ key }) => entity[key])
                    .concat(
                        effectiveTsColumns.map(({ key, precision = 0 }) => {
                            const value = row[header[key]];
                            if (key === 'prediction' || key === 'backfill')
                                return value ? 'yes' : null;
                            if (key === 'real_volume')
                                return roundIfFinite(value, precision);
                            return value;
                        }),
                    ),
            );
        });
    });
    return result;
};

const defaultColumnLabels = {
    Google: 'Google Popularity',
    TikTok: 'TikTok Popularity',
    Instagram: 'Instagram Popularity',
    google: 'Google Popularity',
    tiktok: 'TikTok Popularity',
    instagram: 'Instagram Popularity',
    overview: 'Combined Popularity Prediction',
    google_backfill: 'Google estimated',
    instagram_backfill: 'Instagram estimated',
    tiktok_backfill: 'TikTok estimated',
};

const defaultNonNumericColumns = ['name', 'date'];

const defaultPopularityFormatOptions = {
    columnLabels: defaultColumnLabels,
    nonNumericColumns: defaultNonNumericColumns,
};

const getSortedHeader = (header, nonNumericColumns, columnLabels) => {
    const columnLabelKeys = Object.keys(columnLabels);
    header.sort((a, b) => {
        const aIndex = nonNumericColumns.indexOf(a);
        const bIndex = nonNumericColumns.indexOf(b);
        if (aIndex > -1 && bIndex > -1) return aIndex - bIndex;
        if (aIndex > -1) return -1;
        if (bIndex > -1) return 1;
        const aLabelIndex = columnLabelKeys.indexOf(a);
        const bLabelIndex = columnLabelKeys.indexOf(b);
        if (aLabelIndex > -1 && bLabelIndex > -1)
            return aLabelIndex - bLabelIndex;
        if (aLabelIndex > -1) return -1;
        if (bLabelIndex > -1) return 1;
        return a.localeCompare(b);
    });
    return header;
};

export const formatItemPopularityTimeSeriesForCSV = (
    items,
    {
        columnLabels = defaultColumnLabels,
        nonNumericColumns = defaultNonNumericColumns,
    } = defaultPopularityFormatOptions,
) => {
    if (!(items && items.length)) return [];
    const formattedItems = items.reduce(
        (acc, { name, tsData }) => {
            try {
                const { keys, predictionKeys, stackData, predictionStackData } =
                    getStackedChartFns({ series: tsData });
                const data = [
                    ...stackData,
                    ...predictionStackData?.slice(1),
                ].reduce((innerAcc, { date, ...rest }) => {
                    innerAcc[date] = { ...rest, name };
                    return innerAcc;
                }, {});
                acc.keys.push(...keys, ...predictionKeys);
                acc.data.push(data);
            } finally {
                return acc;
            }
        },
        { keys: nonNumericColumns.slice(), data: [] },
    );
    // Surface per-source backfill flags as trailing "<Source> estimated"
    // yes/blank columns — the multi-source analog of how the single-source
    // export marks `prediction`. Only sources actually flagged somewhere get a
    // column, so a never-backfilled source adds no empty column.
    const estimatedKeys = Array.from(
        formattedItems.data.reduce((acc, dateMap) => {
            Object.values(dateMap).forEach((row) => {
                Object.keys(row).forEach((rowKey) => {
                    if (rowKey.endsWith('_backfill') && row[rowKey])
                        acc.add(rowKey);
                });
            });
            return acc;
        }, new Set()),
    );
    const header = getSortedHeader(
        Array.from(new Set(formattedItems.keys)),
        nonNumericColumns,
        columnLabels,
    ).concat(estimatedKeys);
    const rows = formattedItems.data
        .map((item) =>
            Object.entries(item).reduce((acc, [key, value]) => {
                acc.push(
                    header.map((headerKey) => {
                        if (headerKey.endsWith('_backfill'))
                            return value[headerKey] ? 'yes' : null;
                        return nonNumericColumns.includes(headerKey)
                            ? headerKey === 'date'
                                ? key
                                : value[headerKey]
                            : formatCell(roundIfFinite(value[headerKey], 1));
                    }),
                );
                return acc;
            }, []),
        )
        .flat();
    return [header.map((key) => columnLabels[key] || key), ...rows];
};

const resolveCountryPart = (country) => {
    if (!country) return null;
    if (typeof country === 'string') return country;
    return country.text_id || country.code || country.name || null;
};

export const buildExportFilename = ({
    date,
    country,
    source,
    entity,
    type,
    kind,
} = {}) => {
    const parts = [
        date,
        resolveCountryPart(country),
        source,
        entity,
        type,
        kind,
    ];
    return parts.filter((p) => p != null && p !== '').join(' ');
};

export const downloadItemsAsCSV = (
    filename,
    data,
    columns,
    { titleRow = null } = {},
) => {
    const formattedRows = formatItemsForCSV(data, columns);
    const rows = titleRow ? [[titleRow], ...formattedRows] : formattedRows;
    convertToCsv(`${kebabCase(filename)}.csv`, rows);
};

export const downloadTimeSeriesAsCSV = (filename, ...args) =>
    convertToCsv(
        `${kebabCase(filename)}.csv`,
        formatItemTimeSeriesForCSV(...args),
    );

export const downloadPopularityTimeSeriesAsCSV = (filename, ...args) =>
    convertToCsv(
        `${kebabCase(filename)}.csv`,
        formatItemPopularityTimeSeriesForCSV(...args),
    );
